import { useSwUpdate } from '../lib/swUpdate';

/**
 * Traffic-light status indicator for the header.
 *
 * Priority (highest wins):
 *   red    — N edit(s) failed permanently (sticky until acknowledged)
 *   red    — offline (with N edits queued, if any)
 *   amber  — new SW version ready to load  (click to reload)
 *   amber  — actively retrying a snapshot (transient error)
 *   amber  — currently syncing
 *   amber  — pending writes queued for flush
 *   green  — connected, up to date, nothing pending — "Saved"
 *
 * `online` is required — Workspace supplies it from `apps/vault/lib/useOnline`
 * (true when the vault WS is connected).
 */
export function StatusLight({
  online,
  syncing = false,
  pendingOps = 0,
  retrying = false,
  permanentlyFailed = 0,
  onPermanentClick,
  heartbeatTick = 0,
  lastError,
  offlineLabel = 'Offline',
}: {
  /** True when the app's connectivity probe says we can talk to the server. */
  online: boolean;
  /** True while a sync round-trip is in flight. Defaults to false. */
  syncing?: boolean;
  /** Total unsynced count: outbox ops + pendingSync rows. Defaults to 0. */
  pendingOps?: number;
  /** True while the snapshot retry loop is in transient-failure backoff. */
  retrying?: boolean;
  /**
   * Sticky count of permanently-dropped sync events the user hasn't yet
   * acknowledged. Highest priority — overrides offline/amber/green. Click
   * fires `onPermanentClick` (e.g. open Recover-failed-edits modal).
   */
  permanentlyFailed?: number;
  /** Click handler used when `permanentlyFailed > 0`. */
  onPermanentClick?: () => void;
  /**
   * Increments on every successful WS pong (~15 s cadence while healthy).
   * When the light is green, each tick triggers a brief ripple — visible
   * confirmation we're round-tripping with the server, not just nominally
   * connected. Re-keyed on the counter so each ripple is a fresh element
   * and the CSS animation re-runs cleanly.
   */
  heartbeatTick?: number;
  /** Surfaces in the tooltip when `retrying` is true. */
  lastError?: string;
  /** Tooltip shown when `online === false`. Override for context (e.g. editor). */
  offlineLabel?: string;
}) {
  const { needRefresh, applyUpdate } = useSwUpdate();

  let color: 'green' | 'amber' | 'red' = 'green';
  let label = pendingOps === 0 ? 'Saved' : 'Connected and up to date';
  let onClick: (() => void) | undefined;
  let pulse = false;

  if (permanentlyFailed > 0) {
    color = 'red';
    label = `${permanentlyFailed} edit${permanentlyFailed === 1 ? '' : 's'} failed — click to review`;
    onClick = onPermanentClick;
    pulse = true;
  } else if (!online) {
    color = 'red';
    label =
      pendingOps > 0
        ? `${offlineLabel} — ${pendingOps} edit${pendingOps === 1 ? '' : 's'} queued`
        : offlineLabel;
  } else if (needRefresh) {
    color = 'amber';
    label = 'New version available — click to reload';
    onClick = applyUpdate;
    pulse = true;
  } else if (retrying) {
    color = 'amber';
    label = lastError
      ? `Saving… (last attempt: ${lastError})`
      : 'Saving…';
    pulse = true;
  } else if (syncing) {
    color = 'amber';
    label = 'Syncing…';
    pulse = true;
  } else if (pendingOps > 0) {
    color = 'amber';
    label = `Saving ${pendingOps} edit${pendingOps === 1 ? '' : 's'}…`;
    pulse = true;
  }

  const dotColor =
    color === 'green'
      ? 'bg-emerald-500'
      : color === 'amber'
        ? 'bg-amber-400'
        : 'bg-red-500';
  const ringColor =
    color === 'green'
      ? 'ring-emerald-500/30'
      : color === 'amber'
        ? 'ring-amber-400/30'
        : 'ring-red-500/30';

  const interactive = !!onClick;

  // Heartbeat ripple — only when fully healthy (green, no other pulse). Re-
  // keyed on heartbeatTick so each tick mounts a fresh element and the
  // animation runs from frame 0 every time.
  const showHeartbeat = color === 'green' && !pulse && heartbeatTick > 0;

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      disabled={!interactive}
      className={`relative h-2.5 w-2.5 rounded-full ring-2 ${dotColor} ${ringColor} ${
        interactive ? 'cursor-pointer' : 'cursor-default'
      }`}
    >
      {pulse && (
        <span
          className={`absolute inset-0 rounded-full ${dotColor} animate-ping opacity-60`}
          aria-hidden
        />
      )}
      {showHeartbeat && (
        <span
          key={heartbeatTick}
          className={`absolute inset-0 rounded-full ${dotColor} animate-ping opacity-50`}
          style={{ animationIterationCount: 1, animationDuration: '900ms' }}
          aria-hidden
        />
      )}
    </button>
  );
}
